"use client"

import { Fragment } from "react"
import { ChevronRight } from "lucide-react"
import { indexVar } from "@/components/marketing/landing-v2/cssVars"
import { Mascot } from "@/components/marketing/landing-v2/Mascot"
import { useReveal } from "@/components/marketing/landing-v2/useReveal"
import {
  WORKFLOW_NODE_KINDS,
  type LandingCategoryId,
  type WorkflowNodeKind,
} from "@/lib/landing-v2/content"
import type { Landing2Copy } from "@/lib/landing-v2/copy"

export type WorkflowNode = {
  label: string
  short: string
  kind: WorkflowNodeKind
  caption: string
}

/** Turns the five localized labels plus the shared node shape into rail nodes. */
export function buildWorkflowNodes(
  copy: Landing2Copy,
  categoryId: LandingCategoryId,
): WorkflowNode[] {
  const kinds = copy.workflow.kinds
  let stepIndex = 0

  return copy.workflows[categoryId].map((node, index) => {
    const kind = WORKFLOW_NODE_KINDS[index]
    if (kind === "step") stepIndex += 1

    return {
      ...node,
      kind,
      caption: kind === "step" ? `${kinds.step} 0${stepIndex}` : kinds[kind],
    }
  })
}

function railDescription(nodes: WorkflowNode[]) {
  return nodes.map((node) => node.label).join(" → ")
}

function RailNode({ node, index }: { node: WorkflowNode; index: number }) {
  return (
    <div className="l2-node" data-kind={node.kind} style={indexVar(index)}>
      <span className="l2-node-caption">{node.caption}</span>
      <span className="l2-node-body">
        {node.kind === "agent" ? (
          <Mascot size={22} className="l2-node-mascot" />
        ) : (
          <i className="l2-node-dot" />
        )}
        <span className="l2-node-label">{node.label}</span>
      </span>
    </div>
  )
}

function RailConnector({ index }: { index: number }) {
  return (
    <span className="l2-connector" style={indexVar(index)}>
      <i className="l2-connector-line" />
      <i className="l2-connector-pulse" />
    </span>
  )
}

/**
 * Full-size rail: the page's signature visual. Connectors draw once the rail
 * scrolls in, then a pulse travels the chain on a slow loop.
 */
export function WorkflowRail({ nodes }: { nodes: WorkflowNode[] }) {
  const ref = useReveal<HTMLDivElement>()

  return (
    <div className="l2-rail" ref={ref}>
      <p className="l2-sr">{railDescription(nodes)}</p>
      <div className="l2-rail-track" aria-hidden="true">
        {nodes.map((node, index) => (
          <Fragment key={node.label}>
            {index > 0 && <RailConnector index={index} />}
            <RailNode node={node} index={index} />
          </Fragment>
        ))}
      </div>
    </div>
  )
}

/** Compact rail for the module cards. Wraps instead of scrolling on narrow screens. */
export function WorkflowRailMini({ nodes }: { nodes: WorkflowNode[] }) {
  return (
    <div className="l2-mini-rail">
      <p className="l2-sr">{railDescription(nodes)}</p>
      {nodes.map((node, index) => (
        <Fragment key={node.short}>
          {index > 0 && (
            <ChevronRight
              className="l2-mini-arrow"
              size={12}
              strokeWidth={2}
              aria-hidden="true"
            />
          )}
          <span className="l2-mini-chip" data-kind={node.kind} style={indexVar(index)} aria-hidden="true">
            {node.kind === "agent" && <Mascot size={13} className="l2-mini-mascot" />}
            {node.short}
          </span>
        </Fragment>
      ))}
    </div>
  )
}
